'use client';

import ComparisonTableComponent, { type Column } from '@pixum/comparison-table';
import { DEFAULT_THEME } from '@pixum/theme-provider';
import './styles.css';
import Text, { TEXT_VARIANT, TEXT_HYPHENS, TEXT_TAG } from '@pixum/text';
import { Icon } from '@pixum/icon';
import { ImageLoading } from '@pixum/logo';
import { ButtonVariants } from '@pixum/button';
import { ButtonWithLinkMasking } from '../buttonCollection';
import { RichTextBody } from '../richtText';
import { Image } from '../image';
import type { ComparisonTableProps } from './types';
import { hideTrustedShopBadge } from 'utils';

/**
 * ComparisonTable Component
 *
 * Displays a comparison table with mapped columns from article data.
 *
 * @param props - ComparisonTable component properties
 * @returns Rendered comparison table with header and mapped columns
 */
export function ComparisonTable(props: ComparisonTableProps) {
  const { attributes, columns, title, subtitle, device, subject, description, priceLabel, theme } = props;

  // Map table columns to ComparisonTable format
  const mappedColumns: Column[] = (columns || []).map(column => ({
    id: column.id,
    title: column.title,
    category: column.category
      ? {
          id: column.category.id || column.category.name,
          name: column.category.name
        }
      : undefined,
    isDefault: column.default,
    price: column.price,
    action: column.link ? (
      <ButtonWithLinkMasking link={column.link} device={device} variant={ButtonVariants.PRIMARY} />
    ) : undefined,
    rows: (column.rows || []).map(row => ({
      attribute: {
        key: row.attribute?.key || '',
        label: row.attribute?.label || ''
      },
      text: row.text ? (
        <RichTextBody
          text={row.text}
          variant={TEXT_VARIANT.SUBHEADLINE}
          center
          hyphens={TEXT_HYPHENS.AUTO}
          wrapTextTag={TEXT_TAG.DIV}
        />
      ) : undefined,
      icon: row.icon ? (
        <Icon name={row.icon} loading={ImageLoading.Lazy} color="var(--palette-green-500)" />
      ) : undefined,
      media: row.media ? <Image image={row.media} device={device} loading={ImageLoading.Lazy} /> : undefined
    }))
  }));

  return (
    <div className="comparison-table__wrapper">
      {title && (
        <div className="comparison-table__header">
          <Text variant={TEXT_VARIANT.TITLE_2}>{title}</Text>
          {subtitle && <Text variant={TEXT_VARIANT.SUBHEADLINE}>{subtitle}</Text>}
        </div>
      )}
      <ComparisonTableComponent
        attributes={attributes || []}
        columns={mappedColumns}
        subject={subject || ''}
        caption={description || ''}
        priceLabel={priceLabel || ''}
        device={device}
        theme={theme || DEFAULT_THEME}
        onShowOptions={() => hideTrustedShopBadge(true)}
        onHideOptions={() => hideTrustedShopBadge(false)}
      />
    </div>
  );
}
